/**
 * Shared helpers for rendering Builder.io "page" model entries from the app.
 *
 * Builder pages were historically only reachable through the `/page/$name`
 * catch-all. These helpers let any thin code route (e.g. `/about`,
 * `/how-it-works`) load and describe a Builder page by its target URL, so we
 * can serve marketing copy from Builder at a clean URL while keeping the head
 * (meta + JSON-LD schema) under code control.
 *
 * The Builder SDK and cache layer are pulled in via dynamic import to keep
 * them out of the eager client graph, so this module stays safe to reference
 * from route loaders, which run on either the client or the server.
 *
 * Aliases: builder page loader, builder shim, clean-url builder page
 */

import type { BuilderContent } from "@builder.io/sdk-react/edge";
import { notFound, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { createBrandMeta, createNoIndexMeta } from "~/lib/shared/meta";
import { clientEnv } from "~/lib/utils/client-env";

export const BUILDER_PAGE_MODEL = "page";

/**
 * Builder pages that have moved from the `/page/$name` catch-all to a clean
 * code route. The catch-all 301s these names to their new home, atomically
 * with the deploy that adds the clean route. Extend as pages migrate
 * (see docs/website-2.0/migration.md).
 */
export const MIGRATED_BUILDER_PAGE_REDIRECTS: Record<string, string> = {
  about: "/about",
  // #760: the old member page is superseded by How It Works.
  member: "/how-it-works",
};

/**
 * Builder pages that are conversion/utility surfaces (thank-you screens,
 * email confirmations, internal test pages) — real pages, but with zero
 * search value. The catch-all marks them `noindex, follow`.
 */
export const NOINDEX_BUILDER_PAGE_NAMES = new Set([
  "coach-thank-you",
  "founding-coach-thank-you",
  "for-ccos-thank-you",
  "email-confirmed",
  "booking-call",
  "test-affiliate-tracking",
  "coach-complete-profile",
  "coach-client-workspace-waitlist",
]);

/** Search engines truncate meta descriptions past ~160 chars; hard budget. */
const MAX_META_DESCRIPTION_LENGTH = 160;

/**
 * Brand positioning line appended after the page title in derived fallback
 * descriptions. Mirrors the master `BRAND_DESCRIPTION` phrasing
 * (docs/website-2.0/entity.md) but trimmed to leave room for the title.
 */
const FALLBACK_POSITIONING =
  "Dream Coach Match, the global platform for coaching discovery, transformation, and professional growth.";

/**
 * Generic brand description — last resort for the degenerate case where a
 * page has neither a title nor a name to derive a page-specific fallback from.
 */
const GENERIC_FALLBACK_DESCRIPTION =
  "Find your perfect life or business coach. Connect with certified professionals who can help you achieve your goals.";

/**
 * Derive a deterministic, page-specific fallback description from the page
 * title. A single static fallback repeated across every Builder page whose
 * `description` and `excerpt` are both empty is a duplicate-content signal
 * (#1019) — deriving from the title keeps each page's description unique.
 */
function buildFallbackDescription(pageTitle: string | undefined): string {
  if (!pageTitle) return GENERIC_FALLBACK_DESCRIPTION;

  const description = `${pageTitle} — ${FALLBACK_POSITIONING}`;
  if (description.length <= MAX_META_DESCRIPTION_LENGTH) return description;

  // Pathologically long titles: trim the title, keep the positioning intact.
  // Budget accounts for the " — " separator plus the ellipsis. Truncate by
  // code points (spread, not String#slice) so an emoji or other non-BMP
  // character straddling the cutoff is dropped whole instead of leaving a
  // lone surrogate that renders as a replacement character.
  const titleBudget =
    MAX_META_DESCRIPTION_LENGTH - FALLBACK_POSITIONING.length - 4;
  const truncatedTitle = [...pageTitle].slice(0, titleBudget).join("");
  return `${truncatedTitle}… — ${FALLBACK_POSITIONING}`;
}

/**
 * Minimal schema for the Builder page fields we read in the head. Builder's
 * `BuilderContent` type is complex and non-serializable, so rather than assert
 * `as BuilderContent`, callers pass the loader data as `unknown` and we parse
 * out just the metadata we use.
 */
export const BuilderPageMetadataSchema = z
  .object({
    // Builder's standard entry name lives on the envelope, not in `data` —
    // the fallback for pages whose editors never filled a `data.title`.
    name: z.string().nullish(),
    data: z
      .object({
        title: z.string().nullish(),
        name: z.string().nullish(),
        description: z.string().nullish(),
        excerpt: z.string().nullish(),
        image: z.string().nullish(),
        heroImage: z.string().nullish(),
        keywords: z.array(z.string()).nullish(),
        tags: z.array(z.string()).nullish(),
        // Builder stores a single targeting URL as a string and multiple
        // targeting URLs as an array.
        url: z.union([z.string(), z.array(z.string())]).nullish(),
      })
      .nullish(),
  })
  .nullish();

/**
 * Fetch a Builder "page" entry targeted at `urlPath`. If no page exists, fall
 * back to a Builder `url-redirects` entry (matching `/page/$name` behaviour);
 * otherwise throw a 404. Returns the matched content for use in a loader.
 */
export async function loadBuilderPageContent({
  urlPath,
  search,
}: {
  urlPath: string;
  search: Record<string, string | string[]>;
}): Promise<BuilderContent> {
  const [{ cachedFetchOneEntry }, { getBuilderSearchParams }] =
    await Promise.all([
      import("~/lib/builder/cache"),
      import("@builder.io/sdk-react/edge"),
    ]);

  const content = await cachedFetchOneEntry({
    model: BUILDER_PAGE_MODEL,
    apiKey: clientEnv.VITE_BUILDER_API_KEY,
    userAttributes: { urlPath },
    includeUnpublished: false,
    options: getBuilderSearchParams(search),
  });

  if (!content) {
    const redirectEntry = await cachedFetchOneEntry({
      model: "url-redirects",
      apiKey: clientEnv.VITE_BUILDER_API_KEY,
      query: { "data.sourceUrl": urlPath },
      options: getBuilderSearchParams(search),
    });

    if (redirectEntry?.data) {
      throw redirect({
        to: redirectEntry.data.destinationUrl,
        statusCode: redirectEntry.data.redirectToPermanent ? 301 : 302,
      });
    }

    throw notFound();
  }

  return content;
}

/**
 * Build the meta config for a Builder page from its content, pulling
 * title/description/image from the editor-managed fields and falling back to
 * brand defaults. `canonicalUrl` is supplied by the caller so both clean
 * routes and the `/page/$name` catch-all can reuse this.
 */
export function buildBuilderPageMeta({
  content,
  canonicalUrl,
  noindexFollow = false,
}: {
  content: unknown;
  canonicalUrl: string;
  /** Utility pages: keep out of the index, no canonical, links followed. */
  noindexFollow?: boolean;
}) {
  const parsed = BuilderPageMetadataSchema.safeParse(content);
  const entry = parsed.success ? parsed.data : undefined;
  const pageData = entry?.data;

  if (!pageData) {
    // No Builder content means the loader threw notFound — this head renders
    // on a 404 response. Never emit a canonical or indexable meta for it.
    return createNoIndexMeta(
      "Page Not Found | Dream Coach Match",
      "The page you requested could not be found.",
    );
  }

  // Builder stores the entry name on the envelope (`content.name`); a
  // `data.name` field is kept in the chain for legacy entries that set one.
  const pageTitle = pageData.title || pageData.name || entry?.name || undefined;
  const title = pageTitle || "Dream Coach Match";
  const description =
    pageData.description ||
    pageData.excerpt ||
    buildFallbackDescription(pageTitle);
  const image = pageData.image || pageData.heroImage || undefined;

  return createBrandMeta({
    title,
    description,
    ogImage: image,
    ogUrl: canonicalUrl,
    // A canonical on a noindexed page sends contradictory signals — omit it.
    ...(noindexFollow ? { noindexFollow } : { canonical: canonicalUrl }),
    keywords: pageData.keywords || pageData.tags || undefined,
    ogType: "website",
  });
}
